import React, { useEffect, useState } from 'react';
import { Modal } from 'reactstrap';
import { useDispatch, useSelector } from 'react-redux';
import {
  getProjects as onGetProjects,
  getUnitsByBlock as onGetUnitsByBlock,
  getUnitById as onGetUnitById,
} from '../../slices/thunks';
import { palette } from './contractPaymentsShared';

// Same cascading Project -> Phase -> Block -> Type -> (Apartment Type) ->
// Unit flow as ContractWizard's UnitsStep.js, simplified to a single-unit
// pick (one "+ Add unit" action at a time) instead of a multi-select.
const inputStyle = {
  width: '100%',
  fontSize: 13.5,
  padding: '9px 10px',
  border: `1px solid ${palette.outlineVariant}55`,
  borderRadius: 8,
  background: palette.surfaceContainerLowest,
  outline: 'none',
};

const initialSelection = { project: '', phase: '', block: '', unitType: '', aptType: '' };

const AddUnitPicker = ({ isOpen, toggle, excludeUnitIds = [], onAdd }) => {
  const dispatch = useDispatch();
  const { projects } = useSelector((state) => state.Project);
  const { units } = useSelector((state) => state.Unit);

  const [sel, setSel] = useState(initialSelection);
  const [selectedUnitId, setSelectedUnitId] = useState('');
  const [unitQuery, setUnitQuery] = useState('');

  useEffect(() => {
    if (!isOpen) return;

    setSelectedUnitId('');
    setUnitQuery('');

    // Default the Project/Phase/Block steps to wherever the contract's
    // existing units already live — most contracts have every unit in the
    // same block, so this saves re-clicking through the same three steps
    // every time "+ Add unit" is used.
    let cancelled = false;
    (async () => {
      const projectsList = projects && projects.length ? projects : await dispatch(onGetProjects()).unwrap();

      if (cancelled) return;
      if (!excludeUnitIds.length) {
        setSel(initialSelection);
        return;
      }

      try {
        const detail = await dispatch(onGetUnitById(excludeUnitIds[0])).unwrap();
        if (cancelled) return;

        const projectId = detail?.project?.id;
        const blockId = detail?.block?.id;
        const matchedProject = projectsList.find((p) => String(p.id) === String(projectId));
        const matchedPhase = matchedProject?.phases?.find((ph) =>
          ph.blocks?.some((b) => String(b.id) === String(blockId))
        );

        if (matchedProject && matchedPhase && blockId) {
          setSel({ ...initialSelection, project: matchedProject.id, phase: matchedPhase.id, block: blockId });
          dispatch(onGetUnitsByBlock(blockId));
        } else {
          setSel(initialSelection);
        }
      } catch {
        if (!cancelled) setSel(initialSelection);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [isOpen]);

  const project = projects.find((p) => String(p.id) === String(sel.project));
  const phase = project?.phases?.find((p) => String(p.id) === String(sel.phase));

  const query = unitQuery.trim().toLowerCase();
  const availableUnits = units.filter(
    (u) =>
      u.settlement_price === null &&
      !excludeUnitIds.includes(u.id) &&
      u.unit_type === sel.unitType &&
      (!sel.aptType || u.apt_type === sel.aptType) &&
      (!query || String(u.nr).toLowerCase().includes(query))
  );

  const aptTypes = Array.from(
    new Set(
      units
        .filter((u) => u.unit_type === 'APT' && u.settlement_price === null && !excludeUnitIds.includes(u.id))
        .map((u) => u.apt_type)
        .filter(Boolean)
    )
  ).sort();

  const selectedUnit = units.find((u) => String(u.id) === String(selectedUnitId));

  const handleProjectChange = (value) => {
    setSel({ ...initialSelection, project: value });
    setSelectedUnitId('');
    setUnitQuery('');
  };
  const handlePhaseChange = (value) => {
    setSel((s) => ({ ...s, phase: value, block: '', unitType: '', aptType: '' }));
    setSelectedUnitId('');
    setUnitQuery('');
  };
  const handleBlockChange = (value) => {
    setSel((s) => ({ ...s, block: value, unitType: '', aptType: '' }));
    setSelectedUnitId('');
    setUnitQuery('');
    if (value) dispatch(onGetUnitsByBlock(value));
  };
  const handleUnitTypeChange = (value) => {
    setSel((s) => ({ ...s, unitType: value, aptType: '' }));
    setSelectedUnitId('');
    setUnitQuery('');
  };
  const handleAptTypeChange = (value) => {
    setSel((s) => ({ ...s, aptType: value }));
    setSelectedUnitId('');
    setUnitQuery('');
  };

  const handleConfirm = () => {
    if (!selectedUnit) return;
    onAdd(selectedUnit);
    toggle();
  };

  return (
    <Modal isOpen={isOpen} toggle={toggle} size="lg" contentClassName="border-0 bg-transparent">
      <div
        style={{
          background: palette.surfaceContainerLowest,
          borderRadius: 16,
          overflow: 'hidden',
          boxShadow: '0 24px 48px rgba(25, 28, 30, 0.16)',
        }}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '20px 28px',
            borderBottom: `1px solid ${palette.outlineVariant}33`,
          }}
        >
          <h5 style={{ margin: 0, fontWeight: 800, color: palette.onSurface }}>Add unit to contract</h5>
          <button
            type="button"
            onClick={toggle}
            aria-label="Close"
            style={{ border: 'none', background: 'transparent', color: palette.onSurfaceVariant, fontSize: 20, cursor: 'pointer' }}
          >
            <i className="ri-close-line" />
          </button>
        </div>

        <div style={{ padding: '24px 28px', background: palette.surface, display: 'flex', flexDirection: 'column', gap: 16 }}>
          <div className="row g-3">
            <div className="col-md-6">
              <label className="form-label small fw-semibold" style={{ color: palette.onSurfaceVariant }}>Project</label>
              <select style={inputStyle} value={sel.project} onChange={(e) => handleProjectChange(e.target.value)}>
                <option value="">Choose a project...</option>
                {projects.map((p) => (
                  <option key={p.id} value={p.id}>{p.name}</option>
                ))}
              </select>
            </div>
            <div className="col-md-6">
              <label className="form-label small fw-semibold" style={{ color: palette.onSurfaceVariant }}>Phase</label>
              <select style={inputStyle} value={sel.phase} onChange={(e) => handlePhaseChange(e.target.value)} disabled={!sel.project}>
                <option value="">Choose a phase...</option>
                {project?.phases?.map((p) => (
                  <option key={p.id} value={p.id}>{p.name}</option>
                ))}
              </select>
            </div>
            <div className="col-md-6">
              <label className="form-label small fw-semibold" style={{ color: palette.onSurfaceVariant }}>Block</label>
              <select style={inputStyle} value={sel.block} onChange={(e) => handleBlockChange(e.target.value)} disabled={!sel.phase}>
                <option value="">Choose a block...</option>
                {phase?.blocks?.map((b) => (
                  <option key={b.id} value={b.id}>{b.name}</option>
                ))}
              </select>
            </div>
            <div className="col-md-6">
              <label className="form-label small fw-semibold" style={{ color: palette.onSurfaceVariant }}>Unit type</label>
              <select style={inputStyle} value={sel.unitType} onChange={(e) => handleUnitTypeChange(e.target.value)} disabled={!sel.block}>
                <option value="">Choose a type...</option>
                {Array.from(new Set(units.map((u) => u.unit_type))).map((t) => (
                  <option key={t} value={t}>{t}</option>
                ))}
              </select>
            </div>
            {sel.unitType === 'APT' && (
              <div className="col-md-6">
                <label className="form-label small fw-semibold" style={{ color: palette.onSurfaceVariant }}>
                  Apartment type <span className="text-muted">(optional)</span>
                </label>
                <select style={inputStyle} value={sel.aptType} onChange={(e) => handleAptTypeChange(e.target.value)}>
                  <option value="">All apartment types</option>
                  {aptTypes.map((t) => (
                    <option key={t} value={t}>{t}</option>
                  ))}
                </select>
              </div>
            )}
          </div>

          <div>
            <label className="form-label small fw-semibold" style={{ color: palette.onSurfaceVariant }}>Available units</label>
            {sel.unitType && (
              <input
                value={unitQuery}
                onChange={(e) => setUnitQuery(e.target.value)}
                placeholder="Filter by unit number"
                style={{ ...inputStyle, marginBottom: 9 }}
              />
            )}
            {!sel.unitType ? (
              <div
                style={{
                  border: `1px dashed ${palette.outlineVariant}88`,
                  borderRadius: 11,
                  padding: '22px 18px',
                  textAlign: 'center',
                  fontSize: 13,
                  color: palette.onSurfaceVariant,
                }}
              >
                Complete the steps above to list units
              </div>
            ) : availableUnits.length === 0 ? (
              <div
                style={{
                  border: `1px dashed ${palette.outlineVariant}88`,
                  borderRadius: 11,
                  padding: '22px 18px',
                  textAlign: 'center',
                  fontSize: 13,
                  color: palette.onSurfaceVariant,
                }}
              >
                No available units match this filter.
              </div>
            ) : (
              <div style={{ border: `1px solid ${palette.outlineVariant}55`, borderRadius: 11, maxHeight: 268, overflow: 'auto' }}>
                {availableUnits.map((u) => {
                  const on = String(selectedUnitId) === String(u.id);
                  return (
                    <div
                      key={u.id}
                      onClick={() => setSelectedUnitId(u.id)}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 11,
                        padding: '11px 13px',
                        borderBottom: `1px solid ${palette.outlineVariant}22`,
                        cursor: 'pointer',
                        background: on ? `${palette.primary}0f` : 'transparent',
                      }}
                    >
                      <span
                        style={{
                          flexShrink: 0,
                          width: 15,
                          height: 15,
                          borderRadius: '50%',
                          border: `2px solid ${on ? palette.primary : palette.outlineVariant}`,
                          background: on ? palette.primary : palette.surfaceContainerLowest,
                        }}
                      />
                      <div style={{ minWidth: 0, flex: 1 }}>
                        <div style={{ fontSize: 13.5, fontWeight: 650, color: palette.onSurface }}>
                          {u.unit_type} #{u.nr}{u.apt_type ? ` ${u.apt_type}` : ''}
                        </div>
                        <div style={{ fontSize: 11.5, color: palette.onSurfaceVariant, marginTop: 2 }}>
                          {u.block?.name} · floor {u.floor} · {u.net_sqm} sq.m
                        </div>
                      </div>
                      <span style={{ flexShrink: 0, fontSize: 13.5, fontWeight: 700, color: palette.onSurface }}>
                        €{Number(u.initial_price || 0).toLocaleString('en-US')}
                      </span>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        <div
          style={{
            display: 'flex',
            justifyContent: 'flex-end',
            gap: 12,
            padding: '16px 28px',
            borderTop: `1px solid ${palette.outlineVariant}33`,
            background: palette.surfaceContainerHighest,
          }}
        >
          <button
            type="button"
            onClick={toggle}
            style={{
              padding: '9px 20px',
              borderRadius: 9,
              fontWeight: 700,
              color: palette.onSurfaceVariant,
              background: 'transparent',
              border: `1px solid ${palette.outlineVariant}88`,
              cursor: 'pointer',
            }}
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleConfirm}
            disabled={!selectedUnit}
            style={{
              padding: '9px 20px',
              borderRadius: 9,
              fontWeight: 700,
              color: palette.onPrimary,
              background: selectedUnit ? `linear-gradient(135deg, ${palette.primary}, ${palette.primaryContainer})` : palette.outlineVariant,
              border: 'none',
              cursor: selectedUnit ? 'pointer' : 'not-allowed',
            }}
          >
            Add unit
          </button>
        </div>
      </div>
    </Modal>
  );
};

export default AddUnitPicker;
